import {
  Box,
  Spinner,
  Text,
} from "@chakra-ui/react";
import { useParams } from "react-router-dom";
import TrackingPageLayout from "components/TrackingPageLayout";
import { MapContainer, TileLayer, Marker, Polyline } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { useTripPositions, useTripTracking, type TripPosition } from "hooks/useTrackingTrips";
import { useMemo } from "react";

function toLatLng(pos: TripPosition) {
  return [Number(pos.latitude), Number(pos.longitude)] as [number, number];
}

export default function TrackingTripLiveMapPage() {
  const { tripId } = useParams();
  const { positions, isLoading, error } = useTripPositions(tripId || null);
  const { lastPosition, connected } = useTripTracking(tripId || null);

  const allPositions = useMemo(() => {
    const base = positions || [];
    if (lastPosition) {
      const exists = base.some(
        (p) =>
          Number(p.latitude) === Number(lastPosition.latitude) &&
          Number(p.longitude) === Number(lastPosition.longitude)
      );
      return exists ? base : [...base, lastPosition];
    }
    return base;
  }, [positions, lastPosition]);

  const center = useMemo(() => {
    if (allPositions.length > 0) {
      const last = allPositions[allPositions.length - 1];
      return toLatLng(last);
    }
    return [5.345, -4.024] as [number, number]; // fallback Abidjan
  }, [allPositions]);

  return (
    <TrackingPageLayout
      title={`Carte temps réel – Trajet ${tripId ? tripId.slice(0, 8) + "…" : ""}`}
      withWhiteBox={false}
      action={
        <Text fontSize="sm" color={connected ? "green.600" : "orange.600"}>
          WebSocket : {connected ? "connecté" : "en attente de connexion…"}
        </Text>
      }
    >
      {error && (
        <Text color="red.600" mb={3}>
          Erreur lors du chargement des positions.
        </Text>
      )}

      {isLoading && allPositions.length === 0 ? (
        <Box py={8} display="flex" justifyContent="center">
          <Spinner size="lg" />
        </Box>
      ) : (
        <Box borderRadius="md" overflow="hidden" borderWidth="1px" h="500px">
          <MapContainer
            center={center}
            zoom={13}
            style={{ height: "100%", width: "100%" }}
          >
            <TileLayer
              attribution='&copy; <a href=\"https://www.openstreetmap.org/copyright\">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {allPositions.length > 0 && (
              <>
                <Polyline positions={allPositions.map(toLatLng)} color="blue" />
                <Marker position={center} />
              </>
            )}
          </MapContainer>
        </Box>
      )}
    </TrackingPageLayout>
  );
}

